import React from 'react';
import { Link } from 'react-router-dom';
import { Car, ArrowLeft, Shield, Eye, Lock, Database, Globe, Mail, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const Privacy = () => {
  const sections = [
    {
      icon: Database,
      title: "What We Collect",
      content: `Here's the straight info on what data we gather:

**Account Stuff**
• Name, email, phone number
• Payment info (handled securely by Stripe—we don't store full card numbers)
• Profile photo (if you add one)
• Driver's license and car info (for drivers)

**Trip Info**
• Pickup and drop-off locations
• The route taken
• Date, time, and how long rides take
• Fare and payment details

**Device Info**
• What phone you're using
• Your IP address
• Location when the app is open (kind of essential for a ride-share app)

**Conversations**
• Messages in the app
• Support chats
• Reviews and feedback you leave`
    },
    {
      icon: Eye,
      title: "What We Do With It",
      content: `We use your data to:

**Make Reddi Work**
• Connect you with drivers (or riders)
• Calculate and process payments
• Send you ride confirmations
• Show real-time tracking

**Make Reddi Better**
• Understand how people use the app
• Build new features
• Fix bugs and issues
• Personalize your experience

**Keep Everyone Safe**
• Verify identities
• Run background checks on drivers
• Catch and prevent fraud
• Respond to safety incidents

**Stay in Touch**
• Service updates and notifications
• Respond when you reach out
• Marketing (only with your permission)`
    },
    {
      icon: Globe,
      title: "Who Sees Your Data",
      content: `We share info with:

**Other Users**
• Riders see driver name, photo, car, and rating
• Drivers see rider name, pickup location, and rating
• This is so you can identify each other—pretty important

**Service Providers**
• Stripe (payments)
• Cloud hosting providers
• Analytics tools
• Background check services
• Customer support tools

**When Required by Law**
• Legal requests we're required to respond to
• Protecting rights, safety, or property
• Enforcing our terms

**Business Changes**
If we get acquired or merge with another company, your data might transfer as part of that. We'd let you know.`
    },
    {
      icon: Lock,
      title: "How We Protect It",
      content: `Security isn't an afterthought for us:

**Technical Stuff**
• Encryption everywhere (in transit and at rest)
• Secure login systems
• Regular security testing
• Access monitoring

**People Stuff**
• Team training on data protection
• Only people who need access get it
• Incident response procedures
• Regular policy reviews

Real talk: nothing is 100% secure on the internet. We do our best, but we can't guarantee absolute security.`
    },
    {
      icon: Shield,
      title: "Your Rights",
      content: `You have control over your data:

**Access & Update**
• View your info in the app
• Change your profile and preferences
• Request a copy of your data

**Delete**
• Delete your account through the app or by contacting us
• Request specific data deletion
• Note: We might need to keep some stuff for legal reasons

**Communications**
• Opt out of marketing emails
• Control push notifications
• Unsubscribe from promotional stuff

**Location**
• Toggle location services in your phone settings
• Heads up: location is kind of required for ride-hailing to work

**California Folks**
CCPA gives you extra rights around data access, deletion, and opting out of data sales. For the record: we don't sell your data.`
    },
    {
      icon: Database,
      title: "How Long We Keep It",
      content: `We hold onto data as long as needed to:
• Provide our services
• Meet legal requirements
• Resolve disputes
• Support legitimate business needs

**Typical Timeframes**
• Account data: Until you delete + 7 years (tax/legal stuff)
• Trip data: 7 years
• Support tickets: 3 years after resolved
• Anonymized analytics: Forever

After retention periods, data gets securely deleted or anonymized.`
    },
    {
      icon: Globe,
      title: "Data Transfers",
      content: `Your data might be processed in different countries than where you live. We make sure appropriate protections are in place:
• Standard contractual clauses
• Compliance with data transfer frameworks
• Same security measures regardless of location

By using Reddi, you're okay with data being transferred internationally.`
    },
    {
      icon: Mail,
      title: "Questions?",
      content: `Privacy questions? Reach out:

**Privacy Team**
Reddi, Inc.
privacy@reddi.com

We're actual humans who care about this stuff.`
    }
  ];

  return (
    <div className="min-h-screen bg-[#020617]">
      {/* Header */}
      <header className="py-6 px-6 md:px-12 border-b border-slate-800">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-red-600 flex items-center justify-center transform -skew-x-6">
              <Car className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-['Barlow_Condensed'] font-black text-white">REDDI</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-slate-400 hover:text-white gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <Shield className="w-12 h-12 text-red-500 mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-['Barlow_Condensed'] font-black text-white uppercase mb-4">
            Privacy Policy
          </h1>
          <p className="text-slate-400">
            Last updated: January 2025
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-8 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="p-6 bg-slate-800/50 border border-slate-700 mb-8">
            <p className="text-slate-300 leading-relaxed">
              We take your privacy seriously. This policy explains what data we collect, 
              why we collect it, and what we do with it. No lawyer-speak BS—just straight answers.
            </p>
          </div>
          
          <div className="flex items-start gap-4 p-6 bg-green-600/10 border border-green-600/30 mb-12">
            <AlertCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-green-300 font-medium mb-2">TL;DR</p>
              <p className="text-slate-400">
                We collect what we need to make the app work, keep it safe, and make it better. 
                We don't sell your data. You can delete your account anytime.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-8 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-12">
            {sections.map((section, index) => (
              <div key={index} className="border-b border-slate-800 pb-12 last:border-0">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-red-600/20 rounded-lg flex items-center justify-center">
                    <section.icon className="w-6 h-6 text-red-500" />
                  </div>
                  <h2 className="text-xl font-['Barlow_Condensed'] font-bold text-white uppercase">
                    {section.title}
                  </h2>
                </div>
                <div className="text-slate-400 whitespace-pre-line leading-relaxed pl-16">
                  {section.content}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-slate-800">
        <div className="max-w-7xl mx-auto text-center text-slate-500 text-sm">
          © 2025 REDDI. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default Privacy;
